import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AppAlert, DefaultButton, PrimaryButton } from "../../form";
import FlexBox from "../../layouts/Flexbox";
import ModalBox from "../../modal";
import MultiDateCalendar from "./MultiDateCalendar";
import SelectedTimesReview, {
  type SelectedSlotValidationRef,
} from "./SelectedTimesReview";
import SlotPeriodAccordionPicker from "./SlotPeriodAccordionPicker";
import {
  evaluateSlotSelectionCriteria,
  type SlotCriteriaResult,
} from "./slotCriteria";
import { useHolidayDateKeys } from "../../../hooks/useHolidayDateKeys";
import { useAppSelector } from "../../../store";
import {
  buildSlotSelectionPayload,
  dayjsFromDateKey,
  generateTimeSlots,
  getCalendarReferenceDate,
  sortSelectedDateKeys,
  toSelectedDateKey,
} from "./utils";
import "./style.css";

export type SlotSelectionModalProps = {
  onClose: () => void;
  onSubmit: (payload: unknown) => void;
  oppositeUserTimezone?: string | null;
  userRole?: string;
  blockedDates?: string[];
  onCalendarMonthChange?: (visibleMonthStart: Dayjs) => void;
  initialSelectedTimes?: Array<Date | string>;
  slotIndex?: number;
  slotDetails?: unknown;
  allowMultiSelect?: boolean;
  totalSlots?: number;
};

export default function SlotSelectionModal({
  onClose,
  onSubmit,
  oppositeUserTimezone = null,
  userRole = "vendor",
  blockedDates = [],
  onCalendarMonthChange,
  initialSelectedTimes = [],
}: SlotSelectionModalProps) {
  const theme = useTheme();
  const isNonDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  const timeSlots = useMemo(() => generateTimeSlots(), []);
  const { allowWeekends, minDaysAhead } = useAppSelector((s) => s.UI.appSettings.data);
  const [visibleYear, setVisibleYear] = useState(
    () => getCalendarReferenceDate().year()
  );
  const holidayDates = useHolidayDateKeys(visibleYear);

  const handleCalendarMonthChange = useCallback(
    (visibleMonthStart: Dayjs) => {
      setVisibleYear(visibleMonthStart.year());
      onCalendarMonthChange?.(visibleMonthStart);
    },
    [onCalendarMonthChange]
  );

  const onboardingNotes = useMemo(
    () => [
      ...(allowWeekends ? [] : ["Weekends."]),
      "Past dates.",
      `The next ${minDaysAhead} days.`,
      "Dates highlighted in red indicate no available time slots.",
    ],
    [allowWeekends, minDaysAhead]
  );

  const initialSlotIndicesByDateKey = useMemo(() => {
    if (!initialSelectedTimes || initialSelectedTimes.length === 0) return {};
    const byKey: Record<string, number[]> = {};
    for (const raw of initialSelectedTimes) {
      const maybeDate =
        raw instanceof Date ? raw : typeof raw === "string" ? new Date(raw) : raw;
      const d = dayjs(maybeDate);
      if (!d.isValid()) continue;
      const key = d.startOf("day").format("YYYY-MM-DD");
      const minutes = d.hour() * 60 + d.minute();
      const slotIndex = timeSlots.findIndex(
        (s) => s.minutesFromMidnight === minutes
      );
      if (slotIndex < 0) continue;
      if (!byKey[key]) byKey[key] = [];
      if (!byKey[key].includes(slotIndex)) byKey[key].push(slotIndex);
    }
    Object.keys(byKey).forEach((k) => byKey[k].sort((a, b) => a - b));
    return byKey;
  }, [initialSelectedTimes, timeSlots]);

  const [slotIndicesByDateKey, setSlotIndicesByDateKey] = useState<
    Record<string, number[]>
  >(() => initialSlotIndicesByDateKey);
  const [activeDateKey, setActiveDateKey] = useState<string | null>(null);
  const [validationFocusSlotRef, setValidationFocusSlotRef] =
    useState<SelectedSlotValidationRef | null>(null);

  const validationPassedByCompositeKey = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const dateKey of Object.keys(slotIndicesByDateKey)) {
      const day = dayjsFromDateKey(dateKey);
      const indices = slotIndicesByDateKey[dateKey] ?? [];
      for (const slotIndex of indices) {
        const compositeKey = `${dateKey}:${slotIndex}`;
        map.set(
          compositeKey,
          evaluateSlotSelectionCriteria(
            day,
            timeSlots,
            slotIndex,
            null,
            oppositeUserTimezone
          ).allValid
        );
      }
    }
    return map;
  }, [slotIndicesByDateKey, timeSlots, oppositeUserTimezone]);

  useEffect(() => {
    const dateKeysWithSlots = sortSelectedDateKeys(
      Object.keys(slotIndicesByDateKey)
    ).filter((k) => (slotIndicesByDateKey[k]?.length ?? 0) > 0);
    if (dateKeysWithSlots.length === 0) {
      setValidationFocusSlotRef(null);
      return;
    }
    setValidationFocusSlotRef((previous) => {
      if (previous) {
        const list = slotIndicesByDateKey[previous.dateKey];
        if (list?.includes(previous.slotIndex)) return previous;
      }
      const firstDateKey = dateKeysWithSlots[0];
      const sortedIndices = [
        ...(slotIndicesByDateKey[firstDateKey] ?? []),
      ].sort((a, b) => a - b);
      const firstSlotIndex = sortedIndices[0];
      if (firstSlotIndex === undefined) return null;
      return { dateKey: firstDateKey, slotIndex: firstSlotIndex };
    });
  }, [slotIndicesByDateKey]);

  const activeDayjs =
    activeDateKey !== null ? dayjsFromDateKey(activeDateKey) : null;

  const hasSelectedHolidayDate = useMemo(() => {
    const holidaySet = new Set(holidayDates);
    if (activeDateKey && holidaySet.has(activeDateKey)) {
      return true;
    }
    return Object.keys(slotIndicesByDateKey).some(
      (dateKey) =>
        (slotIndicesByDateKey[dateKey]?.length ?? 0) > 0 &&
        holidaySet.has(dateKey)
    );
  }, [activeDateKey, holidayDates, slotIndicesByDateKey]);

  const slotCriteriaByIndex = useMemo(() => {
    const map = new Map<number, SlotCriteriaResult>();
    if (!activeDayjs) return map;
    for (let i = 0; i < timeSlots.length; i += 1) {
      map.set(
        i,
        evaluateSlotSelectionCriteria(
          activeDayjs,
          timeSlots,
          i,
          null,
          oppositeUserTimezone
        )
      );
    }
    return map;
  }, [activeDayjs, timeSlots, oppositeUserTimezone]);

  const totalSelectedSlotCount = useMemo(() => {
    let count = 0;
    for (const key of Object.keys(slotIndicesByDateKey)) {
      count += slotIndicesByDateKey[key]?.length ?? 0;
    }
    return count;
  }, [slotIndicesByDateKey]);

  const MIN_REQUIRED_SLOTS = 3;
  const meetsMinimumSlotRequirement = totalSelectedSlotCount >= MIN_REQUIRED_SLOTS;
  const hasSelectedSlots = totalSelectedSlotCount > 0;

  const handleConfirm = () => {
    if (!meetsMinimumSlotRequirement) return;
    const keys = sortSelectedDateKeys(Object.keys(slotIndicesByDateKey)).filter(
      (k) => (slotIndicesByDateKey[k]?.length ?? 0) > 0
    );
    const flat = keys.flatMap((key) => {
      const day = dayjsFromDateKey(key).startOf("day");
      const indices = slotIndicesByDateKey[key] ?? [];
      return indices.map((i) => buildSlotSelectionPayload(day, timeSlots, i, null));
    });
    onSubmit(flat);
  };

  const updateSlotIndicesForActiveDate = useCallback(
    (indices: number[]) => {
      if (!activeDateKey) return;
      setSlotIndicesByDateKey((previous) => {
        const next = { ...previous };
        if (indices.length === 0) delete next[activeDateKey];
        else next[activeDateKey] = indices;
        return next;
      });
    },
    [activeDateKey]
  );

  const slotIndicesForActiveDate =
    activeDateKey !== null
      ? slotIndicesByDateKey[activeDateKey] ?? []
      : [];

  const removeDateCompletely = useCallback((dateKey: string) => {
    setSlotIndicesByDateKey((previous) => {
      const next = { ...previous };
      delete next[dateKey];
      return next;
    });
    setValidationFocusSlotRef((current) =>
      current?.dateKey === dateKey ? null : current
    );
    setActiveDateKey((current) => (current === dateKey ? null : current));
  }, []);

  const selectedDatesWithSlots = useMemo(() => {
    const keys = sortSelectedDateKeys(Object.keys(slotIndicesByDateKey));
    return keys.map((k) => dayjsFromDateKey(k).startOf("day"));
  }, [slotIndicesByDateKey]);

  useEffect(() => {
    if (activeDateKey !== null) return;
    const keys = sortSelectedDateKeys(Object.keys(slotIndicesByDateKey)).filter(
      (k) => (slotIndicesByDateKey[k]?.length ?? 0) > 0
    );
    if (keys.length > 0) setActiveDateKey(keys[0]);
  }, [activeDateKey, slotIndicesByDateKey]);

  return (
    <ModalBox
      title={null}
      disableGradient
      onClose={onClose}
      showFooter={false}
      fullScreen={isNonDesktop}
      maxWidth="xl"
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack
          gap={0}
          height={isNonDesktop ? "100%" : "min(78vh, 760px)"}
          overflow="hidden"
        >
          <Stack
            gap={2}
            flex="1 1 0"
            minHeight={0}
            overflow="hidden"
            px={1}
            py={1}
          >
            <Typography
              variant="heading4-helveticaNeue-medium"
              color="text.primary"
              textAlign="left"
              flexShrink={0}
            >
              Schedule Now
            </Typography>
            {isNonDesktop ? (
              <Stack gap={2} flex="1 1 0" minHeight={0} overflow="auto" pr={0.5}>
                <Stack gap={1}>
                  <Typography
                    component="h2"
                    variant="large-helveticaNeue-medium"
                    color="text.primary"
                    flexShrink={0}
                  >
                    Select Date
                  </Typography>
                    <MultiDateCalendar
                      selectedDates={selectedDatesWithSlots}
                      blockedDates={blockedDates}
                      holidayDates={holidayDates}
                      onCalendarMonthChange={handleCalendarMonthChange}
                      activeDate={activeDayjs}
                      onActiveDateChange={(d) =>
                        setActiveDateKey(toSelectedDateKey(d))
                      }
                    />
                </Stack>

                <Stack gap={1}>
                  <Typography
                    component="h2"
                    variant="large-helveticaNeue-medium"
                    color="text.primary"
                    flexShrink={0}
                  >
                    Select Time Slot
                  </Typography>
                    <SlotPeriodAccordionPicker
                      selectedDate={activeDayjs}
                      disabledDatesNotes={onboardingNotes}
                      hideNotes
                      criteriaPresentation="drawer"
                      slots={timeSlots}
                      selectedSlotIndices={slotIndicesForActiveDate}
                      onChange={updateSlotIndicesForActiveDate}
                      slotCriteriaByIndex={slotCriteriaByIndex}
                      oppositeUserTimezone={oppositeUserTimezone}
                      userRole={userRole}
                    />
                </Stack>

                <Stack gap={1}>
                  <Typography
                    component="h2"
                    variant="large-helveticaNeue-medium"
                    color="text.primary"
                    flexShrink={0}
                  >
                    Selected Time Slot
                  </Typography>

                  {!hasSelectedSlots ? (
                    <Paper
                      variant="outlined"
                      className="slotModalPaperOutlined"
                    >
                      <Stack gap={1} p={1.5}>
                        <FlexBox alignItems="center" gap={1}>
                          <InfoOutlinedIcon
                            aria-hidden
                            fontSize="small"
                            color="action"
                            className="slotModalIconFlexShrink"
                          />
                          <Typography
                            variant="body-helveticaNeue-bold"
                            color="text.primary"
                          >
                            Select at least {MIN_REQUIRED_SLOTS} time slots
                          </Typography>
                        </FlexBox>
                        <Typography
                          variant="body-helveticaNeue"
                          color="text.secondary"
                        >
                          Choose a date and pick {MIN_REQUIRED_SLOTS} or more
                          times to enable confirmation.
                        </Typography>
                      </Stack>
                    </Paper>
                  ) : (
                    <SelectedTimesReview
                      slots={timeSlots}
                      selectionsByDateKey={slotIndicesByDateKey}
                      onSelectionsChange={setSlotIndicesByDateKey}
                      validationPassedBySlotKey={
                        validationPassedByCompositeKey
                      }
                      focusedSlotRef={validationFocusSlotRef}
                      onFocusSlot={setValidationFocusSlotRef}
                      onRemoveDateKey={removeDateCompletely}
                    />
                  )}
                </Stack>
              </Stack>
            ) : (
              <Grid
                container
                gap={1}
                alignItems="stretch"
                wrap="nowrap"
                flex="1 1 0"
                minHeight={0}
                minWidth={0}
                overflow="hidden"
              >
                <Grid
                  item
                  xs={3}
                  md={3}
                  lg={3}
                  xl={3}
                  minHeight={0}
                  minWidth={0}
                  overflow="hidden"
                >
                  <Stack
                    gap={1.5}
                    height="100%"
                    minHeight={0}
                    overflow="hidden"
                  >
                    <Typography
                      component="h2"
                      variant="large-helveticaNeue-medium"
                      color="text.primary"
                      flexShrink={0}
                    >
                      Select Date
                    </Typography>
                    <Box
                      flex="1 1 0"
                      minHeight={0}
                      overflow="auto"
                      pr={0.5}
                    >
                      <Stack gap={1.25}>
                          <MultiDateCalendar
                            selectedDates={selectedDatesWithSlots}
                            blockedDates={blockedDates}
                            holidayDates={holidayDates}
                            onCalendarMonthChange={handleCalendarMonthChange}
                            activeDate={activeDayjs}
                            onActiveDateChange={(d) =>
                              setActiveDateKey(toSelectedDateKey(d))
                            }
                          />

                        {activeDayjs !== null &&
                          onboardingNotes &&
                          onboardingNotes.length > 0 ? (
                          <Paper
                            variant="outlined"
                            className="slotModalPaperOutlined"
                          >
                            <Stack gap={1} p={1.5}>
                              <Stack gap={0.75}>
                                <FlexBox alignItems="center" gap={1}>
                                  <BlockOutlinedIcon
                                    aria-hidden
                                    fontSize="small"
                                    color="action"
                                    className="slotModalIconFlexShrink"
                                  />
                                  <Typography
                                    variant="body-helveticaNeue-bold"
                                    color="text.primary"
                                  >
                                    Disabled Dates
                                  </Typography>
                                </FlexBox>
                                <Stack gap={0.75} pl={1.5}>
                                  {onboardingNotes.map((note) => (
                                    <FlexBox
                                      key={note}
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <EventBusyOutlinedIcon
                                        aria-hidden
                                        fontSize="small"
                                        color="action"
                                        className="slotModalIconFlexShrink"
                                      />
                                      <Typography
                                        variant="body-helveticaNeue"
                                        color="text.secondary"
                                      >
                                        {note}
                                      </Typography>
                                    </FlexBox>
                                  ))}
                                </Stack>
                              </Stack>
                            </Stack>
                          </Paper>
                        ) : null}
                        {hasSelectedHolidayDate ? (
                          <AppAlert severity="warning">
                            This date is a US public holiday (ET). Proceed if
                            you are available; otherwise, please select a
                            different day.
                          </AppAlert>
                        ) : null}
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={4}
                  md={4}
                  lg={6}
                  xl={4}
                  minHeight={0}
                  minWidth={0}
                  overflow="hidden"
                >
                  <Stack
                    gap={1.5}
                    className="slotModalGridColumnInner"
                  >
                    <Typography
                      component="h2"
                      variant="large-helveticaNeue-medium"
                      color="text.primary"
                      flexShrink={0}
                    >
                      Select Time Slot
                    </Typography>
                    <Box className="slotModalScrollArea">
                      <SlotPeriodAccordionPicker
                        selectedDate={activeDayjs}
                        disabledDatesNotes={onboardingNotes}
                        slots={timeSlots}
                        selectedSlotIndices={slotIndicesForActiveDate}
                        onChange={updateSlotIndicesForActiveDate}
                        slotCriteriaByIndex={slotCriteriaByIndex}
                        oppositeUserTimezone={oppositeUserTimezone}
                        userRole={userRole}
                      />
                    </Box>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={5}
                  md={5}
                  lg={3}
                  xl={5}
                  minHeight={0}
                  minWidth={0}
                  overflow="hidden"
                >
                  <Stack
                    gap={1.5}
                    className="slotModalGridColumnInner"
                  >
                    <Typography
                      component="h2"
                      variant="large-helveticaNeue-medium"
                      color="text.primary"
                      flexShrink={0}
                    >
                      Selected Time Slot
                    </Typography>
                    <Box className="slotModalScrollArea">
                      {!hasSelectedSlots ? (
                        <Paper
                          variant="outlined"
                          className="slotModalPaperOutlined"
                        >
                          <Stack gap={1} p={1.5}>
                            <FlexBox alignItems="center" gap={1}>
                              <InfoOutlinedIcon
                                aria-hidden
                                fontSize="small"
                                color="action"
                                className="slotModalIconFlexShrink"
                              />
                              <Typography
                                variant="body-helveticaNeue-bold"
                                color="text.primary"
                              >
                                Select at least {MIN_REQUIRED_SLOTS} time slots
                              </Typography>
                            </FlexBox>
                            <Typography
                              variant="body-helveticaNeue"
                              color="text.secondary"
                            >
                              Choose a date and pick {MIN_REQUIRED_SLOTS} or more
                              times to enable confirmation.
                            </Typography>
                          </Stack>
                        </Paper>
                      ) : (
                        <SelectedTimesReview
                          slots={timeSlots}
                          selectionsByDateKey={slotIndicesByDateKey}
                          onSelectionsChange={setSlotIndicesByDateKey}
                          validationPassedBySlotKey={
                            validationPassedByCompositeKey
                          }
                          focusedSlotRef={validationFocusSlotRef}
                          onFocusSlot={setValidationFocusSlotRef}
                          onRemoveDateKey={removeDateCompletely}
                        />
                      )}
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            )}

            {activeDayjs === null && !isNonDesktop ? (
              <Paper variant="outlined" className="slotModalPaperOutlined">
                <Stack gap={1} p={1.5}>
                  <Typography variant="body-helveticaNeue-bold" color="text.primary">
                    Important
                  </Typography>
                  <Stack gap={0.75}>
                    <FlexBox alignItems="center" gap={1}>
                      <LanguageOutlinedIcon
                        aria-hidden
                        fontSize="small"
                        color="action"
                        className="slotModalIconFlexShrink"
                      />
                      <Typography variant="body-helveticaNeue" color="text.secondary">
                        Whether a slot can be selected is based on booking criteria in Eastern Time.
                      </Typography>
                    </FlexBox>
                    <FlexBox alignItems="center" gap={1}>
                      <MyLocationOutlinedIcon
                        aria-hidden
                        fontSize="small"
                        color="action"
                        className="slotModalIconFlexShrink"
                      />
                      <Typography variant="body-helveticaNeue" color="text.secondary">
                        Slots are selected based on your system’s time zone.
                      </Typography>
                    </FlexBox>
                  </Stack>
                </Stack>
              </Paper>
            ) : null}
          </Stack>

          <FlexBox
            component="footer"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            pt={1.5}
            pb={0}
            flexShrink={0}
            borderTop={1}
            borderColor="divider"
            bgcolor="background.paper"
            position={isNonDesktop ? "sticky" : "static"}
            bottom={isNonDesktop ? 0 : undefined}
            zIndex={isNonDesktop ? 2 : undefined}
          >
            {!meetsMinimumSlotRequirement ? (
              <FlexBox alignItems="center" gap={0.75} flex="1 1 0" minWidth={0}>
                <InfoOutlinedIcon
                  aria-hidden
                  fontSize="small"
                  color="primary"
                  className="slotModalIconFlexShrink"
                />
                <Typography
                  variant="body-helveticaNeue"
                  color="primary"
                  className="slotModalFooterHintText"
                >
                  Select at least {MIN_REQUIRED_SLOTS} time slots to confirm.
                </Typography>
              </FlexBox>
            ) : (
              <Box />
            )}

            <FlexBox alignItems="center" gap={2} flexShrink={0}>
              <DefaultButton onClick={onClose}>Cancel</DefaultButton>
              <PrimaryButton
                onClick={handleConfirm}
                disabled={!meetsMinimumSlotRequirement}
              >
                Confirm
              </PrimaryButton>
            </FlexBox>
          </FlexBox>
        </Stack>
      </LocalizationProvider>
    </ModalBox>
  );
}
